import { useMemo, useState } from "react";
import { useNavigate } from "react-router";
import {
  closestCenter,
  DndContext,
  DragOverlay,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragStartEvent,
} from "@dnd-kit/core";
import { Plus, Users, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Sheet,
  SheetContent,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from "@/components/ui/dialog";
import { EmptyState } from "@/components/empty-state";
import { cn } from "@/lib/utils";
import { useLeads } from "@/features/lead/api";
import { useBrokers } from "@/features/appointment/hooks/use-brokers";
import { useAuthStore } from "@/lib/auth-store";
import { seesAllLeads } from "@/features/settings/role-labels";
import {
  ALL_BROKERS_VALUE,
  LEAD_STATUSES,
  LEAD_STATUS_DOT_COLORS,
} from "@/features/lead/constants";
import { LeadCard } from "@/features/lead/components/lead-card";
import { LeadColumn } from "@/features/lead/components/lead-column";
import { LeadCreateForm } from "@/features/lead/components/lead-create-form";
import { LeadKanbanMobile } from "@/features/lead/components/lead-kanban-mobile";
import {
  LeadStatusGuardProvider,
  useLeadStatusGuard,
} from "@/features/lead/components/lead-status-guard-context";
import { useMediaQuery } from "@/features/lead/hooks/use-media-query";
import { NotificationBell } from "@/features/notification/notification-bell";
import type { Lead, LeadStatus } from "@/features/lead/types";

export default function LeadKanbanPage() {
  return (
    <LeadStatusGuardProvider>
      <LeadKanbanContent />
    </LeadStatusGuardProvider>
  );
}

function LeadKanbanContent() {
  const isDesktop = useMediaQuery("(min-width: 768px)");
  const navigate = useNavigate();
  const [brokerFilter, setBrokerFilter] = useState<string>(ALL_BROKERS_VALUE);
  const [activeLead, setActiveLead] = useState<Lead | null>(null);
  const [createForStatus, setCreateForStatus] = useState<LeadStatus | null>(null);

  const query = useLeads(
    brokerFilter === ALL_BROKERS_VALUE
      ? {}
      : { assignedBrokerId: Number.parseInt(brokerFilter, 10) },
  );
  const guard = useLeadStatusGuard();
  const brokersQuery = useBrokers();
  const brokers = brokersQuery.data ?? [];
  // Agents see only their own leads (backend-enforced), so the broker filter
  // is meaningless for them — show it only to OWNER/MANAGER.
  const canViewAllLeads = seesAllLeads(useAuthStore((s) => s.user)?.role);

  // Clicking a card on the kanban navigates to the full detail page
  // at /leads/:id. The old in-page sheet is gone.
  const openLead = (lead: Lead) => {
    navigate(`/leads/${lead.id}`);
  };

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
  );

  // Bucket leads by status. Stable across renders so dnd-kit doesn't
  // see fresh array identities mid-drag.
  const leadsByStatus = useMemo<Record<LeadStatus, Lead[]>>(() => {
    const buckets: Record<LeadStatus, Lead[]> = {
      NEW: [],
      CONTACTED: [],
      VIEWING_SCHEDULED: [],
      NEGOTIATING: [],
      WON: [],
      LOST: [],
    };
    if (!query.data) return buckets;
    for (const lead of query.data) {
      buckets[lead.status].push(lead);
    }
    return buckets;
  }, [query.data]);

  const handleDragStart = (event: DragStartEvent) => {
    const data = event.active.data.current as { lead?: Lead } | undefined;
    if (data?.lead) setActiveLead(data.lead);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    setActiveLead(null);
    if (!event.over) return;
    const activeData = event.active.data.current as { lead?: Lead } | undefined;
    const overData = event.over.data.current as
      | { type: "column"; status: LeadStatus }
      | { type: "lead"; lead: Lead }
      | undefined;
    const lead = activeData?.lead;
    if (!lead || !overData) return;

    const nextStatus: LeadStatus =
      overData.type === "column" ? overData.status : overData.lead.status;
    guard.requestStatusChange(lead, nextStatus);
  };

  if (query.isError) {
    return (
      <EmptyState
        title="Không thể tải danh sách khách hàng"
        description="Vui lòng thử lại."
        action={
          <Button variant="outline" onClick={() => void query.refetch()}>
            Thử lại
          </Button>
        }
      />
    );
  }

  const total = query.data?.length ?? 0;
  const newCount = leadsByStatus.NEW.length;
  const won = query.data?.filter((l) => l.status === "WON").length ?? 0;
  const conversion = total > 0 ? Math.round((won / total) * 100) : 0;
  const funnelTotals = LEAD_STATUSES.map((s) => leadsByStatus[s].length);
  // Shared with the column-header dot palette so the bar and dots can't
  // drift apart. LOST is now visibly rose (not zinc), which matters as
  // soon as the LOST bucket has data.
  const funnelTones = LEAD_STATUSES.map((s) => LEAD_STATUS_DOT_COLORS[s]);

  // Shared broker-filter dropdown — same data on both layouts, just
  // styled differently. Pulled out so the desktop pill and the mobile
  // compact chip stay logically in sync (single source of options).
  const brokerOptions = (
    <SelectContent align="end">
      <SelectItem value={ALL_BROKERS_VALUE}>Tất cả</SelectItem>
      {brokers.map((b) => (
        <SelectItem key={b.id} value={String(b.id)}>
          {b.fullName}
        </SelectItem>
      ))}
    </SelectContent>
  );

  return (
    <div className="flex flex-col gap-3 px-6 py-5">
      {isDesktop ? (
        // Desktop header — eyebrow + title on the left, broker filter
        // pill on the right. Unchanged from before the mobile rework.
        <header className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div className="flex flex-col gap-0.5">
            <p className="text-[12px] text-ink-faint">Pipeline / 30 ngày</p>
            <h1 className="flex items-baseline gap-2 text-[28px] font-bold tracking-tight">
              Khách tiềm năng
              <span className="font-mono text-sm font-medium text-muted-foreground">
                {total}
              </span>
            </h1>
          </div>
          {canViewAllLeads ? (
          <div className="flex flex-wrap items-center gap-2">
            <div className="inline-flex items-center gap-2 rounded-lg border border-border bg-card px-3 py-1.5 text-[12px]">
              <span className="text-muted-foreground">Môi giới</span>
              <Select value={brokerFilter} onValueChange={setBrokerFilter}>
                <SelectTrigger className="h-auto w-auto gap-1 border-0 bg-transparent px-0 py-0 text-[12px] font-semibold shadow-none focus-visible:ring-0">
                  <SelectValue />
                </SelectTrigger>
                {brokerOptions}
              </Select>
            </div>
          </div>
          ) : null}
        </header>
      ) : (
        // Mobile header — drops the eyebrow per design. Row 1 carries the
        // title with the "+ Thêm" CTA and the notification bell; row 2
        // pairs the counts with the (owner/manager-only) broker filter.
        // Splitting the rows keeps the 28px title untruncated on narrow
        // phones — three controls beside it didn't fit. The button
        // defaults the new lead to NEW since that's the column you'd
        // manually add into ~all the time; brokers who want a different
        // starting status change it on the next screen.
        <header className="flex flex-col gap-1.5">
          <div className="flex items-center justify-between gap-3">
            <h1 className="min-w-0 truncate text-[28px] font-bold leading-tight tracking-tight">
              Pipeline
            </h1>
            <div className="flex shrink-0 items-center gap-2">
              <Button
                type="button"
                className="h-9 gap-1 rounded-lg px-3"
                onClick={() => setCreateForStatus("NEW")}
              >
                <Plus aria-hidden="true" className="size-4" />
                <span className="text-[14px] font-semibold">Thêm</span>
              </Button>
              <NotificationBell />
            </div>
          </div>
          <div className="flex items-center justify-between gap-2">
            <p className="font-mono text-[12px] text-muted-foreground">
              {total} leads · {newCount} mới
            </p>
            {canViewAllLeads ? (
              <Select value={brokerFilter} onValueChange={setBrokerFilter}>
                <SelectTrigger
                  aria-label="Lọc theo môi giới"
                  // Compact pill — icon + selected name, max-width caps a
                  // long broker name with truncation.
                  className="h-7 w-auto max-w-[140px] gap-1.5 rounded-lg border-border bg-card px-2.5 text-[12px] font-semibold"
                >
                  <Users
                    aria-hidden="true"
                    className="size-3.5 shrink-0 text-muted-foreground"
                  />
                  <SelectValue />
                </SelectTrigger>
                {brokerOptions}
              </Select>
            ) : null}
          </div>
        </header>
      )}

      {isDesktop ? (
        // Desktop funnel bar — labelled with a "PHỄU KHÁCH" eyebrow and a
        // "% chốt" conversion readout on the right. Borrowed wholesale
        // from the previous layout.
        <div className="flex items-center gap-3 rounded-lg border border-border bg-card px-3 py-2">
          <span className="font-mono text-[11px] tracking-wider text-ink-faint">PHỄU KHÁCH</span>
          <div className="flex h-2 flex-1 items-stretch gap-0.5">
            {funnelTotals.map((v, i) => (
              <div
                key={i}
                className={cn(
                  "rounded-sm transition-opacity",
                  v === 0 ? "opacity-30" : "",
                  funnelTones[i],
                )}
                style={{ flex: Math.max(v, 0.4) }}
                aria-hidden="true"
              />
            ))}
          </div>
          <span className="font-mono text-[11px] text-ink-faint tabular-nums">
            {conversion}% chốt
          </span>
        </div>
      ) : (
        // Mobile funnel — strip-only, no chrome. The eyebrow label
        // and the "% chốt" readout were removed per design feedback;
        // the bar is the entire signal here. We give it a touch more
        // height than desktop (2.5px → 2px*1.25) so the colour reads
        // at a glance on a phone.
        <div
          aria-label={`Phễu khách · ${conversion}% chốt`}
          role="img"
          className="flex h-1.5 items-stretch gap-0.5"
        >
          {funnelTotals.map((v, i) => (
            <div
              key={i}
              className={cn(
                "rounded-sm transition-opacity",
                v === 0 ? "opacity-30" : "",
                funnelTones[i],
              )}
              style={{ flex: Math.max(v, 0.4) }}
              aria-hidden="true"
            />
          ))}
        </div>
      )}

      {query.isPending ? (
        <KanbanSkeleton />
      ) : isDesktop ? (
        // Desktop — drag-and-drop horizontal grid. Six columns share the
        // viewport at md+; below that we never render this branch.
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
          onDragCancel={() => setActiveLead(null)}
        >
          <div className="-mx-6 grid auto-cols-[minmax(220px,_1fr)] grid-flow-col gap-2.5 overflow-x-auto px-6 pb-4 md:auto-cols-fr">
            {LEAD_STATUSES.map((status) => (
              <LeadColumn
                key={status}
                status={status}
                leads={leadsByStatus[status]}
                enableDrag={isDesktop}
                onOpenLead={openLead}
                onAddLead={setCreateForStatus}
              />
            ))}
          </div>

          <DragOverlay>
            {activeLead ? (
              <LeadCard lead={activeLead} enableDrag={false} onOpen={() => undefined} />
            ) : null}
          </DragOverlay>
        </DndContext>
      ) : (
        // Mobile — swipe-able one-column-per-page carousel. DnD is
        // skipped entirely; status changes happen via the detail page's
        // change-status sheet.
        <LeadKanbanMobile
          leadsByStatus={leadsByStatus}
          onOpenLead={openLead}
          onAddLead={setCreateForStatus}
        />
      )}


      <LeadCreateSurface
        status={createForStatus}
        isDesktop={isDesktop}
        onClose={() => setCreateForStatus(null)}
      />
    </div>
  );
}

interface LeadCreateSurfaceProps {
  status: LeadStatus | null;
  isDesktop: boolean;
  onClose: () => void;
}

/**
 * Renders the create-lead modal (desktop) or bottom sheet (mobile).
 *
 * Custom header: eyebrow `Mới · Khách tiềm năng` + title
 * `Thêm khách tiềm năng` + an `Esc` keycap pill + a close X button. The
 * form supplies its own footer so the Dialog/Sheet is flushed (`p-0`,
 * `gap-0`, `showCloseButton={false}`) and the modal width caps at
 * 860px to fit the two-column body. Mirrors the appointment-create
 * modal shell at appointment-calendar-page.tsx.
 */
function LeadCreateSurface({ status, isDesktop, onClose }: LeadCreateSurfaceProps) {
  if (status === null) return null;
  const body = (
    <LeadCreateForm
      mode={{ kind: "with-phone-lookup", initialStatus: status }}
      onSuccess={onClose}
      onCancel={onClose}
    />
  );
  const headerEyebrow = (
    <p className="font-mono text-[12px] text-muted-foreground">
      Mới · Khách tiềm năng
    </p>
  );
  const escKeycap = (
    <span
      aria-hidden="true"
      className="inline-flex h-6 items-center rounded-md border border-border bg-muted/60 px-1.5 font-mono text-[11px] font-semibold text-ink-soft"
    >
      Esc
    </span>
  );
  const closeButton = (
    <button
      type="button"
      onClick={onClose}
      aria-label="Đóng"
      className="flex size-8 items-center justify-center rounded-lg border border-border bg-background text-muted-foreground hover:bg-muted"
    >
      <X aria-hidden="true" className="size-3.5" />
    </button>
  );
  if (isDesktop) {
    return (
      <Dialog open={status !== null} onOpenChange={(o) => (!o ? onClose() : undefined)}>
        <DialogContent
          className="max-h-[88vh] gap-0 overflow-y-auto p-0 sm:max-w-[860px]"
          showCloseButton={false}
        >
          <div className="flex items-center justify-between border-b border-border px-5 py-4">
            <div>
              {headerEyebrow}
              <DialogTitle className="mt-0.5 text-[16px] font-bold tracking-tight">
                Thêm khách tiềm năng
              </DialogTitle>
            </div>
            <div className="flex items-center gap-2">
              {escKeycap}
              {closeButton}
            </div>
          </div>
          {body}
        </DialogContent>
      </Dialog>
    );
  }
  return (
    <Sheet open={status !== null} onOpenChange={(o) => (!o ? onClose() : undefined)}>
      <SheetContent
        side="bottom"
        className="max-h-[90vh] gap-0 overflow-y-auto p-0"
        showCloseButton={false}
      >
        <div className="flex items-center justify-between border-b border-border px-5 py-4">
          <div>
            {headerEyebrow}
            <SheetTitle className="mt-0.5 text-[16px] font-bold tracking-tight">
              Thêm khách tiềm năng
            </SheetTitle>
          </div>
          {closeButton}
        </div>
        {body}
      </SheetContent>
    </Sheet>
  );
}

function KanbanSkeleton() {
  return (
    <div className="-mx-4 flex gap-3 overflow-x-auto px-4 pb-4 md:gap-4">
      {LEAD_STATUSES.map((s) => (
        <div
          key={s}
          className="w-72 shrink-0 rounded-xl border border-border bg-muted/40 p-3 md:w-[280px]"
        >
          <Skeleton className="h-6 w-24" />
          <div className="mt-3 flex flex-col gap-2">
            <Skeleton className="h-20 w-full rounded-lg" />
            <Skeleton className="h-20 w-full rounded-lg" />
            <Skeleton className="h-20 w-full rounded-lg" />
          </div>
        </div>
      ))}
    </div>
  );
}
